import type {
  PoTemplateFieldDefinitionItemFragment,
  PoTemplatePredefinedFieldItemFragment,
} from 'generated/resources/graphql'

import { fieldLabel } from './translations'
import { toSectionType, type MergedField } from './types'

/**
 * Merges the predefined catalog with the stored field definitions.
 * - All predefined fields are always shown (switch on/off based on stored match).
 * - Custom (non-predefined) stored fields are appended at the end of their section.
 */
export function mergeFields(
  catalog: PoTemplatePredefinedFieldItemFragment[],
  stored: PoTemplateFieldDefinitionItemFragment[]
): MergedField[] {
  const storedByKey = new Map<string, PoTemplateFieldDefinitionItemFragment>()
  const customFields: PoTemplateFieldDefinitionItemFragment[] = []

  for (const field of stored) {
    if (field.predefined && field.fieldKey) {
      storedByKey.set(`${field.sectionType}.${field.fieldKey}`, field)
    } else {
      customFields.push(field)
    }
  }

  const merged: MergedField[] = []

  // When the version already has stored predefined fields it is an existing
  // template. Any catalog field that is missing from the DB was added after
  // the version was created and should default to invisible so it doesn't
  // suddenly appear as active. For brand-new templates (no stored predefined
  // fields at all) we use the catalog defaults so the template starts with
  // the intended configuration. Locked catalog fields always use the catalog
  // defaults so the always-visible/required invariant is preserved on the
  // client even before the backend self-heals on save.
  const isExistingVersion = storedByKey.size > 0

  // Add all predefined catalog fields
  for (const catalogField of catalog) {
    const match = storedByKey.get(catalogField.id)
    const useCatalogDefaults = !isExistingVersion || catalogField.locked
    merged.push({
      key: catalogField.id,
      sectionType: toSectionType(catalogField.sectionType),
      fieldKey: catalogField.fieldKey,
      displayLabel: fieldLabel(catalogField.labelKey),
      fieldType: catalogField.fieldType,
      predefined: true,
      visible: match ? match.visible : useCatalogDefaults ? catalogField.visible : false,
      required: match ? match.required : useCatalogDefaults ? catalogField.required : false,
      computed: catalogField.computed,
      locked: catalogField.locked,
      position: match?.position ?? catalogField.position,
      storedId: match?.id ?? null,
      defaultValue: match?.defaultValue ?? null,
      options: match?.options ?? [],
    })
  }

  // Append custom fields
  for (const field of customFields) {
    merged.push({
      key: field.id,
      sectionType: toSectionType(field.sectionType),
      fieldKey: field.fieldKey ?? field.id,
      displayLabel: field.label,
      fieldType: field.fieldType,
      predefined: false,
      visible: field.visible,
      required: field.required,
      computed: field.computed,
      locked: false,
      position: field.position,
      storedId: field.id,
      defaultValue: field.defaultValue ?? null,
      options: field.options ?? [],
    })
  }

  return merged
}
